import React, { useEffect, useState } from 'react';
import InputTabs from '../../../components/InputTabs';
import { MenuSeparator } from '../../../components/Menu';
import { isAttachmentUploaded } from '../../../utils/AttachmentUploader';
import { useQuill, useQuillReadOnly } from '../../QuillContext';
import { useDownloadAttachments } from '../../attachment/DownloadAttachmentsProvider';
import type AttachmentMention from '../../formats/AttachmentMention';
import { getDefaultAttachmentMentionText } from '../../formats/AttachmentMention';
import ConvertIntoEmbedMenuItem from '../ConvertIntoEmbedMenuItem';
import { editLinkIcon } from '../editorIcons';
import AddAttachmentMenuItem from './AddAttachmentMenuItem';
import DeleteMenuItem from './DeleteMenuItem';
import DownloadAllMenuItem from './DownloadAllMenuItem';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import useBlotHighlightedClass from './useBlotHighlightedClass';

interface MentionTextEditorProps {
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}

const MentionTextEditor = ({
  initialValue,
  onSave,
  onCancel,
}: MentionTextEditorProps) => {
  const [value, setValue] = useState(initialValue);
  return (
    <InputTabs.Root>
      <InputTabs.Input
        value={value}
        onChange={setValue}
        onEscape={onCancel}
        onSave={() => onSave(value)}
        placeholder={initialValue}
        saveDisabled={value === initialValue}
      />
    </InputTabs.Root>
  );
};

interface AttachmentMentionToolbarOverflowMenuProps {
  blot: AttachmentMention;
  handleDownloadAll: (() => Promise<void>) | null;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const AttachmentMentionToolbarOverflowMenu = ({
  blot,
  handleDownloadAll,
}: AttachmentMentionToolbarOverflowMenuProps) => {
  const { quill } = useQuill();

  return (
    <>
      {quill && (
        <>
          <AddAttachmentMenuItem
            onUpload={(e) => {
              blot.appendAttachment(Array.from(e.currentTarget.files ?? []));
            }}
          />
          <ConvertIntoEmbedMenuItem blot={blot} quill={quill} />
        </>
      )}
      {handleDownloadAll && (
        <DownloadAllMenuItem handleDownloadAll={handleDownloadAll} />
      )}
      <MenuSeparator />
      <DeleteMenuItem
        onClick={() => {
          blot.remove();
        }}
      >
        Delete all files
      </DeleteMenuItem>
    </>
  );
};

interface AttachmentMentionToolbarProps {
  blot: AttachmentMention;
}

const AttachmentMentionToolbar = ({ blot }: AttachmentMentionToolbarProps) => {
  const downloadAttachments = useDownloadAttachments();
  const [isEditingMentionText, setIsEditingMentionText] = useState(false);
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();
  const formats = blot.formats();

  const visibleAttachments = blot.getVisibleAttachments();
  const downloadableAttachments =
    visibleAttachments.filter(isAttachmentUploaded);

  const handleDownloadAll =
    downloadableAttachments.length > 0 && downloadAttachments
      ? () => downloadAttachments(downloadableAttachments)
      : null;

  useBlotHighlightedClass(blot);

  useEffect(() => {
    setIsEditingMentionText(false);
  }, [blot]);

  if (!visibleAttachments.length) return null;

  if (isEditingMentionText) {
    const initialValue =
      blot.getMentionText() ??
      getDefaultAttachmentMentionText(visibleAttachments);

    return (
      <MentionTextEditor
        initialValue={initialValue}
        onSave={(value) => {
          if (!quill) return;
          if (!value) {
            blot.format('mention-text', null);
          } else {
            blot.format('mention-text', value);
          }
          setIsEditingMentionText(false);
        }}
        onCancel={() => setIsEditingMentionText(false)}
      />
    );
  }

  if (readOnly) return null;

  return (
    <Toolbar
      hideEditButton
      overflowMenu={
        <ToolbarOverflowMenu>
          <AttachmentMentionToolbarOverflowMenu
            blot={blot}
            handleDownloadAll={handleDownloadAll}
          />
        </ToolbarOverflowMenu>
      }
    >
      <ToolbarButtonGroup>
        <ToolbarButton
          icon={editLinkIcon}
          tooltip="Edit"
          onClick={() => {
            setIsEditingMentionText(true);
          }}
        />
      </ToolbarButtonGroup>
      <ToolbarCommentButtonGroup
        blot={blot}
        existingComment={formats.comment}
      />
    </Toolbar>
  );
};

export default AttachmentMentionToolbar;
